/**
 * Aurora 파일 업로드 서비스
 * re.disco.www의 discoImageUploader와 동일한 패턴
 *
 * 2단계 플로우:
 *   1. authorize_server('aurora') → 인증
 *   2. POST aurora/home/disco_image_uploader/ → 파일 업로드 → save_name 반환
 */

/**
 * Aurora에 파일 업로드
 * @param {string} func - 업로드 용도 ('sale', 'owner_verification', 'broker_auth' 등)
 * @param {File[]} files - 업로드할 파일 배열
 * @param {string} [prefix] - 저장 경로 prefix (예: verification uuid)
 * @returns {Promise<Array<{origin_name: string, save_name: string}>>} 업로드 결과
 */
export function uploadToAurora(func, files, prefix) {
  return new Promise((resolve, reject) => {
    const doUpload = () => {
      const auroraUrl = window.server_by_hostname?.("aurora", "home/disco_image_uploader/")
        || "https://aurora.disco.re/home/disco_image_uploader/";

      const form = new FormData();
      form.append("func", func);
      if (prefix) form.append("ref_uuid", prefix);

      files.forEach((file) => {
        if (file && typeof file === "object") {
          form.append("imglist", file, _genHashedFileName(file));
        }
      });

      const xhr = new XMLHttpRequest();
      xhr.withCredentials = true;
      xhr.open("POST", auroraUrl);
      xhr.onload = () => {
        try {
          const result = JSON.parse(xhr.responseText);
          if (result.state === true || result.state === "true") {
            resolve(result.items || []);
          } else {
            resolve([]);
          }
        } catch {
          reject(new Error("aurora response parse error"));
        }
      };
      xhr.onerror = () => reject(new Error("aurora upload error"));
      xhr.send(form);
    };

    if (typeof window.authorize_server === "function") {
      window.authorize_server("aurora", doUpload);
    } else {
      doUpload();
    }
  });
}

/**
 * 단일 파일 업로드 후 save_name 반환
 * @param {string} func - 업로드 용도
 * @param {File} file - 업로드할 파일
 * @param {string} [prefix] - 저장 경로 prefix
 * @returns {Promise<string|null>} save_name 또는 null
 */
export async function uploadFileToAurora(func, file, prefix) {
  const items = await uploadToAurora(func, [file], prefix);
  if (!items.length) return null;
  // 단일 파일 업로드이므로 첫 번째 성공 항목 사용
  const success = items.find((item) => item.state === true || item.state === 'true' || item.save_name);
  return success?.save_name || items[0]?.save_name || null;
}

/**
 * 업로드 결과에서 원본 파일에 매칭되는 save_name 찾기
 * discoImageUploadParser와 동일
 */
export function findSaveName(file, items) {
  const hashedName = _genHashedFileName(file);
  const found = items.find((item) => item.origin_name === hashedName);
  return found?.save_name || null;
}

function _genHashedFileName(file) {
  const hash =
    typeof window.MD5 === "function"
      ? window.MD5(file.name + " " + file.size)
      : _simpleHash(file.name + " " + file.size);
  const ext = file.name.split(".").pop();
  return hash + "." + ext;
}

function _simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return Math.abs(hash).toString(16).padStart(8, "0");
}
